import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, EyeIcon, CopyIcon, CheckIcon } from 'lucide-react';
export function AuditLog() {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [apiMethod, setApiMethod] = useState('');
  const [channel, setChannel] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [copyType, setCopyType] = useState<'request' | 'response' | null>(null);
  // Sample API methods
  const apiMethods = [{
    id: '',
    name: 'All APIs'
  }, {
    id: 'createUser',
    name: 'Create User'
  }, {
    id: 'updateUser',
    name: 'Update User'
  }, {
    id: 'deleteUser',
    name: 'Delete User'
  }, {
    id: 'createRole',
    name: 'Create Role'
  }, {
    id: 'updateRole',
    name: 'Update Role'
  }, {
    id: 'deleteRole',
    name: 'Delete Role'
  }, {
    id: 'getTransactions',
    name: 'Get Transactions'
  }, {
    id: 'processPayment',
    name: 'Process Payment'
  }, {
    id: 'generateReport',
    name: 'Generate Report'
  }];
  // Sample channels
  const channels = [{
    id: '',
    name: 'All Channels'
  }, {
    id: 'mobile',
    name: 'Mobile'
  }, {
    id: 'ib',
    name: 'Internet Banking'
  }, {
    id: 'otc',
    name: 'Over the Counter'
  }];
  // Sample API audit log data
  const sampleApiAuditData = [{
    id: 1,
    date: '2023-05-10',
    time: '14:30:22',
    methodName: 'createUser',
    methodDisplayName: 'Create User',
    statusCode: 200,
    statusDescription: 'Success',
    channel: 'ib',
    channelDisplayName: 'Internet Banking',
    requestJson: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      role: 'Operator',
      status: 'active'
    },
    responseJson: {
      success: true,
      userId: 12345,
      message: 'User created successfully'
    }
  }, {
    id: 2,
    date: '2023-05-10',
    time: '14:45:15',
    methodName: 'updateRole',
    methodDisplayName: 'Update Role',
    statusCode: 200,
    statusDescription: 'Success',
    channel: 'ib',
    channelDisplayName: 'Internet Banking',
    requestJson: {
      roleId: 5,
      name: 'Operator',
      permissions: ['dashboard_view', 'transactions_view'],
      status: 'active'
    },
    responseJson: {
      success: true,
      roleId: 5,
      message: 'Role updated successfully'
    }
  }, {
    id: 3,
    date: '2023-05-10',
    time: '15:10:47',
    methodName: 'processPayment',
    methodDisplayName: 'Process Payment',
    statusCode: 400,
    statusDescription: 'Bad Request',
    channel: 'mobile',
    channelDisplayName: 'Mobile',
    requestJson: {
      senderId: 1001,
      receiverId: 2002,
      amount: 5000,
      currency: 'ETB',
      narration: 'Payment for services'
    },
    responseJson: {
      success: false,
      errorCode: 'INSUFFICIENT_FUNDS',
      message: 'Insufficient funds in sender account'
    }
  }, {
    id: 4,
    date: '2023-05-10',
    time: '15:30:33',
    methodName: 'getTransactions',
    methodDisplayName: 'Get Transactions',
    statusCode: 200,
    statusDescription: 'Success',
    channel: 'mobile',
    channelDisplayName: 'Mobile',
    requestJson: {
      accountId: 1001,
      fromDate: '2023-05-01',
      toDate: '2023-05-10',
      page: 1,
      pageSize: 10
    },
    responseJson: {
      success: true,
      totalCount: 15,
      transactions: [{
        id: 1,
        date: '2023-05-10',
        amount: 5000,
        type: 'debit',
        description: 'Payment'
      }, {
        id: 2,
        date: '2023-05-09',
        amount: 10000,
        type: 'credit',
        description: 'Deposit'
      }]
    }
  }, {
    id: 5,
    date: '2023-05-09',
    time: '09:30:22',
    methodName: 'deleteUser',
    methodDisplayName: 'Delete User',
    statusCode: 403,
    statusDescription: 'Forbidden',
    channel: 'otc',
    channelDisplayName: 'Over the Counter',
    requestJson: {
      userId: 123,
      reason: 'No longer with company'
    },
    responseJson: {
      success: false,
      errorCode: 'PERMISSION_DENIED',
      message: 'You do not have permission to delete users'
    }
  }, {
    id: 6,
    date: '2023-05-09',
    time: '10:45:33',
    methodName: 'generateReport',
    methodDisplayName: 'Generate Report',
    statusCode: 200,
    statusDescription: 'Success',
    channel: 'ib',
    channelDisplayName: 'Internet Banking',
    requestJson: {
      reportType: 'transaction',
      fromDate: '2023-05-01',
      toDate: '2023-05-09',
      format: 'pdf'
    },
    responseJson: {
      success: true,
      reportId: 45678,
      downloadUrl: '/reports/45678.pdf'
    }
  }];
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Validate date range
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 90) {
        alert('Date range cannot exceed 90 days');
        setIsLoading(false);
        return;
      }
      if (from > to) {
        alert('From date must be before To date');
        setIsLoading(false);
        return;
      }
    }
    // Simulate API delay
    setTimeout(() => {
      // Filter sample data based on search criteria
      let results = [...sampleApiAuditData];
      // Filter by API method if selected
      if (apiMethod) {
        results = results.filter(log => log.methodName === apiMethod);
      }
      // Filter by channel if selected
      if (channel) {
        results = results.filter(log => log.channel === channel);
      }
      // Sort by date and time (newest first)
      results.sort((a, b) => {
        const dateA = new Date(`${a.date} ${a.time}`);
        const dateB = new Date(`${b.date} ${b.time}`);
        return dateB.getTime() - dateA.getTime();
      });
      setSearchResults(results);
      setHasSearched(true);
      setIsLoading(false);
    }, 800);
  };
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setApiMethod('');
    setChannel('');
    setSearchResults([]);
    setHasSearched(false);
  };
  const handleViewDetails = (log: any) => {
    setSelectedLog(log);
    setShowDetails(true);
  };
  const handleCopyToClipboard = (type: 'request' | 'response') => {
    if (!selectedLog) return;
    const textToCopy = JSON.stringify(type === 'request' ? selectedLog.requestJson : selectedLog.responseJson, null, 2);
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setCopyType(type);
      setTimeout(() => {
        setCopied(false);
        setCopyType(null);
      }, 2000);
    });
  };
  const getStatusBadgeClass = (statusCode: number) => {
    if (statusCode >= 200 && statusCode < 300) {
      return 'bg-green-100 text-green-800';
    } else if (statusCode >= 400 && statusCode < 500) {
      return 'bg-yellow-100 text-yellow-800';
    } else if (statusCode >= 500) {
      return 'bg-red-100 text-red-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  };
  const getChannelBadgeClass = (channel: string) => {
    switch (channel) {
      case 'mobile':
        return 'bg-blue-100 text-blue-800';
      case 'ib':
        return 'bg-purple-100 text-purple-800';
      case 'otc':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-178">
      <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-179">API Audit Log</h1>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6" data-id="element-180">
        <form onSubmit={handleSearch} className="space-y-6" data-id="element-181">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-182">
            <div className="space-y-2" data-id="element-183">
              <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700" data-id="element-184">
                From Date
              </label>
              <input id="fromDate" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-185" />
            </div>
            <div className="space-y-2" data-id="element-186">
              <label htmlFor="toDate" className="block text-sm font-medium text-gray-700" data-id="element-187">
                To Date
              </label>
              <input id="toDate" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" required data-id="element-188" />
            </div>
            <div className="space-y-2" data-id="element-189">
              <label htmlFor="apiMethod" className="block text-sm font-medium text-gray-700" data-id="element-190">
                API Method
              </label>
              <select id="apiMethod" value={apiMethod} onChange={e => setApiMethod(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-191">
                {apiMethods.map(method => <option key={method.id} value={method.id} data-id="element-192">
                    {method.name}
                  </option>)}
              </select>
            </div>
            <div className="space-y-2" data-id="element-193">
              <label htmlFor="channel" className="block text-sm font-medium text-gray-700" data-id="element-194">
                Channel
              </label>
              <select id="channel" value={channel} onChange={e => setChannel(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-195">
                {channels.map(ch => <option key={ch.id} value={ch.id} data-id="element-196">
                    {ch.name}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-197">
            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-[#F58220] text-white rounded-md font-medium hover:bg-[#e07619] focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:ring-opacity-50 flex items-center" data-id="element-198">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-199">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-200"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-201"></path>
                  </svg>
                  Searching...
                </> : <>
                  <SearchIcon size={16} className="inline mr-2" data-id="element-202" />
                  Search
                </>}
            </button>
            <button type="button" onClick={handleReset} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50" data-id="element-203">
              Reset
            </button>
          </div>
        </form>
      </div>
      {hasSearched && <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-204">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-205">
            <h3 className="font-medium text-[#1A1F36]" data-id="element-206">
              API Audit Log Results
            </h3>
            {searchResults.length > 0 && <div className="flex items-center gap-3" data-id="element-207">
                <button onClick={() => alert('Exporting as CSV...')} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-208">
                  <DownloadIcon size={16} className="mr-2" data-id="element-209" />
                  Export CSV
                </button>
              </div>}
          </div>
          {searchResults.length === 0 ? <div className="p-8 text-center" data-id="element-210">
              <p className="text-gray-500" data-id="element-211">
                No API audit logs found matching your search criteria.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-212">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-213">
                <thead className="bg-gray-50" data-id="element-214">
                  <tr data-id="element-215">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-216">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-217">
                      Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-218">
                      Method Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-219">
                      Status Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-220">
                      Status Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-221">
                      Channel
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-222">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-223">
                  {searchResults.map(log => <tr key={log.id} className="hover:bg-gray-50" data-id="element-224">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-225">
                        {log.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-226">
                        {log.time}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-227">
                        {log.methodDisplayName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-228">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(log.statusCode)}`} data-id="element-229">
                          {log.statusCode}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-230">
                        {log.statusDescription}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-231">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getChannelBadgeClass(log.channel)}`} data-id="element-232">
                          {log.channelDisplayName}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-233">
                        <button onClick={() => handleViewDetails(log)} className="text-blue-600 hover:text-blue-800 flex items-center" data-id="element-234">
                          <EyeIcon size={16} className="mr-1" data-id="element-235" />
                          View
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
      {/* JSON Details Modal */}
      {showDetails && selectedLog && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-236">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-6 max-h-[90vh] overflow-auto" data-id="element-237">
            <div className="flex justify-between items-center mb-4" data-id="element-238">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-239">
                API Request/Response Details
              </h3>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700" data-id="element-240">
                &times;
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-241">
              <div data-id="element-242">
                <p className="text-sm text-gray-500" data-id="element-243">Method</p>
                <p className="text-sm font-medium" data-id="element-244">
                  {selectedLog.methodDisplayName}
                </p>
              </div>
              <div data-id="element-245">
                <p className="text-sm text-gray-500" data-id="element-246">Status</p>
                <p className="text-sm font-medium flex items-center" data-id="element-247">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full mr-2 ${getStatusBadgeClass(selectedLog.statusCode)}`} data-id="element-248">
                    {selectedLog.statusCode}
                  </span>
                  {selectedLog.statusDescription}
                </p>
              </div>
              <div data-id="element-249">
                <p className="text-sm text-gray-500" data-id="element-250">Channel</p>
                <p className="text-sm font-medium" data-id="element-251">
                  {selectedLog.channelDisplayName}
                </p>
              </div>
            </div>
            <div className="space-y-6" data-id="element-252">
              <div data-id="element-253">
                <div className="flex justify-between items-center mb-2" data-id="element-254">
                  <h4 className="text-md font-medium text-[#1A1F36]" data-id="element-255">
                    Request JSON
                  </h4>
                  <button onClick={() => handleCopyToClipboard('request')} className="text-blue-600 hover:text-blue-800 flex items-center text-sm" data-id="element-256">
                    {copied && copyType === 'request' ? <>
                        <CheckIcon size={14} className="mr-1" data-id="element-257" />
                        Copied!
                      </> : <>
                        <CopyIcon size={14} className="mr-1" data-id="element-258" />
                        Copy to Clipboard
                      </>}
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-259">
                  <pre className="text-xs text-gray-800" data-id="element-260">
                    {JSON.stringify(selectedLog.requestJson, null, 2)}
                  </pre>
                </div>
              </div>
              <div data-id="element-261">
                <div className="flex justify-between items-center mb-2" data-id="element-262">
                  <h4 className="text-md font-medium text-[#1A1F36]" data-id="element-263">
                    Response JSON
                  </h4>
                  <button onClick={() => handleCopyToClipboard('response')} className="text-blue-600 hover:text-blue-800 flex items-center text-sm" data-id="element-264">
                    {copied && copyType === 'response' ? <>
                        <CheckIcon size={14} className="mr-1" data-id="element-265" />
                        Copied!
                      </> : <>
                        <CopyIcon size={14} className="mr-1" data-id="element-266" />
                        Copy to Clipboard
                      </>}
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-md overflow-x-auto" data-id="element-267">
                  <pre className="text-xs text-gray-800" data-id="element-268">
                    {JSON.stringify(selectedLog.responseJson, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-269">
              <button onClick={() => setShowDetails(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300" data-id="element-270">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
}